import { computed, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LocalStorageService } from '@core/state/local-storage.service';
import { NotifyService } from '@core/notify/notify.service';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { AuthService } from '@app/services/auth.service';
import { UserService } from '@app/services/user.service';
import { StorageService } from './storage.service';
import { HttpClient } from '@angular/common/http';
import { AuthGoogleService } from '@app/services/auth-google.service';
import { AuthAppleService } from '@app/services/auth-apple.service';
import { UserResponse } from '@app/types/user.types';

@Injectable({ providedIn: 'root' })
export class SessionService {
  public readonly user = computed(() => this.storage.user());

  private _inited = false;
  private readonly USER_CACHE_KEY = 'current_user';

  constructor(
    private readonly _router: Router,
    private readonly _notify: NotifyService,
    private readonly _authService: AuthService,
    private readonly _userService: UserService,
    private readonly localStorage: LocalStorageService,
    private readonly storage: StorageService,
    private http: HttpClient,
    private readonly authGoogleService: AuthGoogleService,
    private readonly authAppleService: AuthAppleService
  ) {}

  async init(): Promise<void> {
    if (this._inited) return;
    this._inited = true;

    const token = this.localStorage.get<string>('access_token');
    const cached = this.localStorage.get<UserResponse>(this.USER_CACHE_KEY);

    if (cached) this.storage.updateUser(cached);

    if (!token) {
      this.storage.updateAuthenticated(false);
      return;
    }

    this.storage.updateAuthenticated(true);

    try {
      const u = await this.getUserFromBack(true);
      if (u) this.localStorage.set(this.USER_CACHE_KEY, u);
      // A transient /user/me failure must not discard a valid session token. The auth interceptor handles 401s.
    } catch {
      // Keep the session during transient initialization failures.
    }
  }

  public login(email: string, password: string) {
    return firstValueFrom(
      this._authService.login({ email, password }).pipe(take(1))
    ).then(
      (res) => {
        this.handleLoginResponse(res);
        return res;
      },
      (error) => {
        this.handleLoginError(error, { suppressToast: true });
        throw error;
      }
    );
  }

  public loginWithToken(token: string) {
    return firstValueFrom(this._authService.loginWithToken(token).pipe(take(1))).then(
      (res) => {
        this.handleLoginResponse(res);
        return res;
      },
      (error) => {
        this.handleLoginError(error, { suppressToast: true });
        throw error;
      }
    );
  }

  public loginWithGoogleCredential(credential: string) {
    return firstValueFrom(
      this.authGoogleService.loginWithGoogleCredential(credential).pipe(take(1))
    ).then(
      (res) => {
        this.handleLoginResponse(res);
      },
      (error) => {
        this.handleLoginError(error, { suppressToast: true });
        throw error;
      }
    );
  }

  public loginWithAppleCredential(credential: string) {
    return firstValueFrom(
      this.authAppleService.loginWithAppleCredential(credential).pipe(take(1))
    ).then(
      (res) => {
        this.handleLoginResponse(res);
        return res;
      },
      (error) => {
        this.handleLoginError(error, { suppressToast: true });
        throw error;
      }
    );
  }

  public validateTransferToken(t: string) {
    return firstValueFrom(this._authService.loginTransferValidate(t).pipe(take(1))).then(
      async (res) => {
        this.localStorage.set('access_token', res.token);
        this.storage.updateAuthenticated(true);

        try {
          const u = await this.getUserFromBack(true);
          if (u) this.localStorage.set(this.USER_CACHE_KEY, u);
        } catch {}

        return res;
      },
      (error) => {
        this.handleLoginError(error, { suppressToast: true });
        throw error;
      }
    );
  }

  public logout(): void {
    (this as any)._loggingOut ??= false;
    if ((this as any)._loggingOut) return;
    (this as any)._loggingOut = true;

    this.storage.updateAuthenticated(false);
    this.storage.clear();
    this.localStorage.clearExcept(['vapt.theme', 'appLang'], ['appLang_']);

    this._authService.logout().pipe(take(1)).subscribe({ next: () => {}, error: () => {} });

    this._router
      .navigate(['/login'], { replaceUrl: true })
      .finally(() => {
        (this as any)._loggingOut = false;
      });
  }

  public async getUser() {
    if (this.storage.user() != null) return this.storage.user();
    return await this.getUserFromBack();
  }

  public getUserFromBack(silent = false) {
    return firstValueFrom(this._userService.getUser().pipe(take(1))).then(
      (res) => {
        const user = ((res as any)?.data ?? res) as UserResponse;
        this.storage.updateUser(user);
        this.localStorage.set(this.USER_CACHE_KEY, user);
        return user;
      },
      (err): any => {
        if (!silent) this._notify.apiError(err, 'COMMON.GENERIC_ERROR');
        return null;
      }
    );
  }

  private handleLoginResponse(res: any): void {
    const token =
      res?.token ??
      res?.accessToken ??
      res?.data?.token ??
      res?.data?.accessToken;

    if (!token) {
      this._notify.warning('Token não recebido no login');
      return;
    }

    this.localStorage.set('access_token', token);

    const refreshToken =
      res?.refresh_token ??
      res?.refreshToken ??
      res?.data?.refresh_token ??
      res?.data?.refreshToken;

    if (refreshToken) {
      this.localStorage.set('refresh_token', refreshToken);
    }

    this.storage.updateAuthenticated(true);

    const u: UserResponse | undefined = res?.user ?? res?.data?.user ?? undefined;

    if (u) {
      this.storage.updateUser(u);
      this.localStorage.set(this.USER_CACHE_KEY, u);
    } else {
      this.getUserFromBack(true).then((user) => {
        if (user) this.localStorage.set(this.USER_CACHE_KEY, user);
      }).catch(() => {});
    }
  }

  private handleLoginError(err: any, { suppressToast = false } = {}): void {
    if (!suppressToast) this._notify.apiError(err, 'COMMON.GENERIC_ERROR');
  }

  public currentUser() {
    return this.storage.user();
  }
}
